/**
 * Date Service — Wrapper centralizado para dayjs
 *
 * Substitui o uso direto de moment.js.
 * Todos os plugins necessários já estão configurados aqui.
 *
 * MIGRAÇÃO: Para migrar de moment → date service:
 *   // Antes:
 *   import moment from 'moment'
 *   moment(date).format('DD/MM/YYYY')
 *
 *   // Depois:
 *   import { date } from '@shared/services/date'
 *   date(date).format('DD/MM/YYYY')
 *
 * Equivalências comuns:
 *   moment(d).format(f)              → date(d).format(f)
 *   moment().startOf('day')          → date().startOf('day')
 *   moment().endOf('month')          → date().endOf('month')
 *   moment(a).diff(b, 'days')        → date(a).diff(date(b), 'day')
 *   moment(a).isBefore(b)            → date(a).isBefore(date(b))
 *   moment(a).isAfter(b)             → date(a).isAfter(date(b))
 *   moment(a).isBetween(b, c)        → date(a).isBetween(date(b), date(c))
 *   moment(a).add(1, 'days')         → date(a).add(1, 'day')
 *   moment(a).subtract(1, 'months')  → date(a).subtract(1, 'month')
 *   moment(a).isValid()              → date(a).isValid()
 *   moment(a).toDate()               → date(a).toDate()
 *   moment.months()                  → date.months()
 *   moment.monthsShort()             → date.monthsShort()
 *   moment.duration(ms)              → date.duration(ms)
 *
 * ⚠️ Diferenças de API dayjs vs moment:
 *   - diff() retorna o mesmo sinal, mas requer dayjs() nos argumentos
 *   - 'days' → 'day', 'months' → 'month', 'years' → 'year' (singular)
 *   - duration().format() requer plugin duration instalado
 */

import dayjs from 'dayjs'
import 'dayjs/locale/pt-br'
import isBetween from 'dayjs/plugin/isBetween'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import duration from 'dayjs/plugin/duration'
import localeData from 'dayjs/plugin/localeData'
import utc from 'dayjs/plugin/utc'
import weekday from 'dayjs/plugin/weekday'
import relativeTime from 'dayjs/plugin/relativeTime'
import advancedFormat from 'dayjs/plugin/advancedFormat'

// Registrar todos os plugins
dayjs.extend(isBetween)
dayjs.extend(isSameOrBefore)
dayjs.extend(isSameOrAfter)
dayjs.extend(customParseFormat)
dayjs.extend(duration)
dayjs.extend(localeData)
dayjs.extend(utc)
dayjs.extend(weekday)
dayjs.extend(relativeTime)
dayjs.extend(advancedFormat)

// Locale padrão: pt-BR
dayjs.locale('pt-br')

/**
 * Factory principal — substitui moment()
 * @param {string|Date|dayjs.Dayjs|null} [value]
 * @returns {dayjs.Dayjs}
 */
export function date(value) {
  return dayjs(value)
}

// Métodos estáticos — substitui moment.months(), moment.duration(), etc.
date.months = () => dayjs.months()
date.monthsShort = () => dayjs.monthsShort()
date.duration = (value, unit) => dayjs.duration(value, unit)
date.isDayjs = value => dayjs.isDayjs(value)
date.locale = locale => dayjs.locale(locale)
date.utc = value => dayjs.utc(value)
date.now = () => dayjs().valueOf()

export default date
